import type { FailedPendingErrorModel } from '@/shared/generated'
import type { MutateOptions, UseMutationResult } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import type { AffectedOGsResponse } from './affected-ogs-types'

import { useCallback, useRef } from 'react'
import axios from 'axios'
import { useTranslation } from 'react-i18next'

import { useModals } from '@/shared/lib/modal-manager'

type Options = {
	onRetry?: () => void
	onCancel?: () => void
}

export function useWithAffectedOGsHandler<TData, TError, TVariables, TContext>(
	mutation: UseMutationResult<TData, TError, TVariables, TContext>,
	options?: Options
): UseMutationResult<TData, TError, TVariables, TContext> {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const originalMutateAsyncRef = useRef(mutation.mutateAsync)
	const handle424Error = useCallback(
		(
			error: AxiosError<AffectedOGsResponse>,
			variables: TVariables
		): Promise<TData> =>
			new Promise((resolve, reject) => {
				if (!axios.isAxiosError(error) || error.response?.status !== 424) {
					return reject(error)
				}
				const affectedOGs = error.response.data
					.detail as FailedPendingErrorModel[]
				const ids = affectedOGs.map((item) => item.optimization_group_id)
				const errorMessages = affectedOGs.reduce(
					(acc, item) => ({
						...acc,
						[item.optimization_group_id]: item.msg,
					}),
					{} as Record<string, string>
				)

				showModal('AFFECTED_OPTIMIZATION_GROUPS_MODAL', {
					title: t('global.pc.affect.modal.title'),
					subtitle: t('global.pc.affect.modal.subtitle'),
					optimizationGroupIds: ids,
					errorMessages,
					onConfirm: () => {
						options?.onRetry?.()
						// Retry mutation - if it fails with 424 again, recursively show modal
						// If it fails with non-424 error, swallow it (mutation state already updated)
						resolve(
							originalMutateAsyncRef
								.current(variables)
								.catch((retryError) => handle424Error(retryError, variables))
						)
					},
					onCancel: () => {
						options?.onCancel?.()
						reject(error)
					},
				})
			}),
		[showModal, options]
	)
	const wrappedMutate = useCallback(
		(
			variables: TVariables,
			mutationOptions?: MutateOptions<TData, TError, TVariables, TContext>
		) => {
			originalMutateAsyncRef
				.current(variables, {
					onError: () => {
						// Don't call global error handler - we intercept 424 before it reaches consumers
					},
				})
				.then((data) => {
					// Pass undefined for onMutateResult and context - we don't have access to these in the wrapper
					mutationOptions?.onSuccess?.(data, variables, undefined, {} as any)
				})
				.catch((error) => {
					if (!axios.isAxiosError(error) || error.response?.status !== 424) {
						mutationOptions?.onError?.(error, variables, undefined, {} as any)
					} else {
						handle424Error(error, variables)
							.then((data) => {
								mutationOptions?.onSuccess?.(
									data,
									variables,
									undefined,
									{} as any
								)
							})
							.catch(() => {
								// User cancelled - mutation remains in error state, no callback needed
							})
					}
				})
		},
		[handle424Error]
	)
	const wrappedMutateAsync = useCallback(
		async (
			variables: TVariables,
			mutationOptions?: MutateOptions<TData, TError, TVariables, TContext>
		) => {
			try {
				return await originalMutateAsyncRef.current(variables, mutationOptions)
			} catch (error) {
				if (!axios.isAxiosError(error) || error.response?.status !== 424) {
					throw error
				}

				return handle424Error(error, variables)
			}
		},
		[handle424Error]
	)

	return {
		...mutation,
		mutate: wrappedMutate as typeof mutation.mutate,
		mutateAsync: wrappedMutateAsync as typeof mutation.mutateAsync,
	}
}
